const moment = require('moment-timezone')

const freightExchangeModel = {
    user: null,
    filters: {
        vehicle_type_ids: null,
        vehicle_size_ids: null,
        date_from: null,
        date_to: null,
        from_weight: null,
        to_weight: null,
        with_double_trailer: false,
        with_trailer: false,
        along_route: false,
        saved: true,
        name: 'GeoFleet Freight Exchange',
        destination_location: null,
        destination_radius: 10
    },
    offers: [],
    last_search: { id: null }
}

// Helper function to get initial state from config, sessionStorage, or model
const getInitialState = (rootState) => {
    // Priority 1: Check config from geoFleet module (if rootState is available))
    if (rootState?.config?.user?.freightExchangeFilters) {
        return { ...freightExchangeModel, ...rootState.config.user.freightExchangeFilters }
    }

    // Priority 2: Check sessionStorage
    try {
        const sessionStorageData = JSON.parse(sessionStorage.getItem('freight_exchange_filters'))
        if (sessionStorageData && Object.keys(sessionStorageData).length > 0) {
            return { ...freightExchangeModel, filters: sessionStorageData }
        } else {
            return freightExchangeModel
        }
    } catch (e) {
        // Ignore parsing errors
    }

    // Priority 3: Return default model
    return freightExchangeModel
}

export default {
    state: (rootState) => getInitialState(rootState),
    getters: {
        getFEFilters: (state) => {
            return state.filters
        },
        getFeFiltersforSearchPayload: (state, getters, rootState) => {
            // If the state has date_from and date_to valued (not null)
            // return the DateString (+ timezone) like 2025-12-31T23:00:00+02:00. The timezone is the timezone of the user.

            // Get user timezone or default to Europe/Rome
            const userTimezone = rootState?.loggedUser?.params?.tz || 'Europe/Rome'

            // Helper function to convert date to string with timezone
            const formatDateWithTimezone = (date) => {
                if (!date) return null

                // If date is already a string (from sessionStorage), parse it first
                // Assume string dates are in UTC (from JSON.stringify)
                const dateObj = typeof date === 'string' ? moment.utc(date) : moment(date)

                if (!dateObj.isValid()) return null

                // Convert to user timezone and format as YYYY-MM-DDTHH:mm:ss+HH:mm (e.g., 2025-12-31T23:00:00+02:00)
                return moment.tz(dateObj, userTimezone).format('YYYY-MM-DDTHH:mm:ssZZ')
            }

            const { destination_location, destination_radius, ...searchFilters } =
                state.filters
            const payload = {
                ...searchFilters,
                name: state.filters.name == '' ? 'GeoFleet Freight Exchange' : state.filters.name,
                date_from: formatDateWithTimezone(state.filters.date_from),
                date_to: formatDateWithTimezone(state.filters.date_to)
            }
            return payload
        },
        getFEUser: (state) => state.user,
        getFEOffers: (state) => state.offers,
        getFELastSearch: (state) => state.last_search
    },
    mutations: {
        setFEFilters(state, filters) {
            state.filters.vehicle_type_ids = filters.vehicle_type_ids
            state.filters.vehicle_size_ids = filters.vehicle_size_ids
            state.filters.date_from = filters.date_from
            state.filters.date_to = filters.date_to
            state.filters.from_weight = filters.from_weight
            state.filters.to_weight = filters.to_weight
            state.filters.destination_location = filters.destination_location || null
            state.filters.destination_radius = Math.max(
                1,
                Math.min(50, Number(filters.destination_radius) || 10)
            )
        },
        setLastSearch(state, lastSearch) {
            state.last_search = lastSearch
        },
        setFreightExchangeUser(state, user) {
            state.user = user
        },
        setFreightExchangeOffers(state, offers) {
            state.offers = offers
        }
    },
    actions: {
        async saveFEFilters({ commit, dispatch }, payload) {
            const { filters } = payload

            // Execute mutation
            commit('setFEFilters', filters)

            // Save to sessionStorage
            sessionStorage.setItem('freight_exchange_filters', JSON.stringify(filters))

            // Sprint 19 (REFACTOR-030): scrittura granulare via Config Bus (shim
            // `saveConfig` ritirato). Persist debounced + flush su unload (flushPending).
            await dispatch(
                'config/commitChange',
                { key: 'freightExchangeFilters', value: filters },
                { root: true }
            )
        },
        async saveFELastSearch({ commit, dispatch }, payload) {
            const { lastSearch } = payload

            commit('setLastSearch', lastSearch)

            await dispatch(
                'config/commitChange',
                { key: 'freightExchangeLastSearch', value: lastSearch },
                { root: true }
            )
        },
        initializeLastSearchFromConfig({ commit, rootState }) {
            // Sprint 19 — FIX mismatch chiave: il writer salva `freightExchangeLastSearch`
            // ma qui si leggeva `last_search` (chiave mai scritta) → l'init non trovava MAI
            // il valore salvato e committava sempre {} (azzerando l'initial { id: null }).
            // Ora legge la chiave giusta e committa solo se presente.
            const lastSearchFromConfig = rootState?.config?.user?.freightExchangeLastSearch
            if (lastSearchFromConfig) {
                commit('setLastSearch', lastSearchFromConfig)
            }
        },
        initializeFiltersFromConfig({ commit, state, rootState }) {
            const filtersFromConfig = rootState?.config?.user?.freightExchangeFilters || {}
            const filtersFromSessionStorage =
                JSON.parse(sessionStorage.getItem('freight_exchange_filters')) || {}

            // Check if config has freightExchangeFilters and state doesn't have them initialized
            const configFilters = filtersFromConfig || filtersFromSessionStorage
            if (configFilters) {
                // Check if current state is empty/default
                const isStateEmpty =
                    !state.filters.date_from &&
                    !state.filters.date_to &&
                    !state.filters.vehicle_type_ids &&
                    !state.filters.vehicle_size_ids &&
                    !state.filters.from_weight &&
                    !state.filters.to_weight

                // If state is empty and config has filters, initialize from config
                if (isStateEmpty) {
                    commit('setFEFilters', configFilters)

                    // Also update sessionStorage to keep them in sync
                    sessionStorage.setItem(
                        'freight_exchange_filters',
                        JSON.stringify(configFilters)
                    )
                }
            }
        },
        async setAndLoadFEOffers({ commit, dispatch }, offers) {
            // First, set the offers in state
            commit('setFreightExchangeOffers', offers)

            // Then, load markers for all offers
            await dispatch('loadFEOffers')
        },
        async loadFEOffers({ state, dispatch }) {
            // Iterate through all offers
            await dispatch('removeAllMarkers')
            for (const offer of state.offers) {
                // Check if transportFreight and spots exist
                if (offer.transportFreight && offer.transportFreight.spots) {
                    // Find all loading spots
                    const [loadingSpots, unloadingSpots] = offer.transportFreight.spots

                    await dispatch(
                        'drawMarker',
                        {
                            center: [loadingSpots.lat, loadingSpots.long],
                            title: `${loadingSpots.locality} - ${unloadingSpots.locality}`,
                            icon_unicode: '#xe046', // Package icon for freight
                            ref: `freight-exchange-${offer.id}-${loadingSpots.locality}`,
                            data: offer,
                            reference: () => { },
                            customizer: {
                                color_class:
                                    loadingSpots.type === 'loading'
                                        ? 'freight-exchange-pickup'
                                        : 'freight-exchange-delivery',
                                size_x: 31
                            }
                        },
                        { root: true }
                    )
                }
            }
        }
    }
}
